<script setup>
import apiDriveCms from "@/services/http/api-drive/cms/index";
import apiDriveMagasin from "@/services/http/api-drive/magasin";
import merchApiDrive from "@/services/http/api-drive/magasin/merch";
import { useTrackingPage } from "@/mixins/tracking/all-pages";
import blogMagasinApiDrive from "@/services/http/api-drive/magasin/blog-magasin";
import { loadPageWithLoader } from "@/utils/webpack-helper";
import { isConsentGivenFor } from "@/utils/consentements.js";
import { useContextStore } from "@/store/context";
import { useMagasinStore } from "@/store/magasin";
import { usePanierStore } from "@/store/panier";
import { StatusTypes, useAuthStore } from "@/store/auth";
import { useClientStore } from "@/store/client";
import { useDatalayerStore } from "@/store/datalayer";
import { useAxiosHelper } from "@/utils/axios-helper";
import { isMagasinBascule } from "@/utils/magasin";
import { getMagasinLink } from "@/utils/link-carrefour";

const { layout } = storeToRefs(useContextStore());
const { id: currentMagasin, isBascule } = storeToRefs(useMagasinStore());
const { nbProduits } = storeToRefs(usePanierStore());
const { status: authStatus } = storeToRefs(useAuthStore());
const { client } = storeToRefs(useClientStore());
const route = useRoute();
const { throwIfAxiosError } = useAxiosHelper();
const refreshMagasin = ref(false);

const component = computed(() => {
  return loadPageWithLoader(
    lazyComponents[`/components/pages/fiche-magasin/${layout.value}.vue`]
  );
});

const isFlocklerLoaded = computed(() => {
  return isConsentGivenFor("C0003");
});

const {
  displayChangeShopPopin,
  displayOrCloseStoreLocatorPopin,
  switchMagasin
} = useMagasinStore();

const { trackPage } = useTrackingPage();

onMounted(() => {
  trackPage(trackingCodes.PAGETYPE.shop);
});

let id = route.params.id;
if (parseInt(id) === parseInt(useRuntimeConfig().public.MAGASINS_NATIONAL)) {
  id = currentMagasin.value;
}

let prospectus = [];
let shopPushZones = [];
let articlesBlog = [];
let magasin = {};
let aliasWeb = null;
let fuel = [];

const { data: mag, error } = await useAsyncData(
  (app) => app.$apiDrive.use(apiDriveMagasin).magasin(id),
  {
    watch: [refreshMagasin]
  }
);
if (mag.value && mag.value.data) {
  magasin = mag.value.data;
}

throwIfAxiosError(error);
if (magasin.attributes && magasin.attributes.aliasWeb) {
  aliasWeb = magasin.attributes.aliasWeb;
}

if (isMagasinBascule(magasin)) {
  await navigateTo(getMagasinLink(magasin) || "https://www.carrefour.fr", {
    external: true,
    redirectCode: 301
  });
}

if (aliasWeb && route.params.slug !== aliasWeb) {
  const newPath = route.path.replace(route.params.slug, aliasWeb);
  await navigateTo(newPath, { replace: true, redirectCode: 301 });
}

const { data: fuelData } = await useAsyncData((app) =>
  app.$apiDrive.use(apiDriveCms).fetchFuel(id)
);
if (fuelData.value && fuelData.value.data) {
  fuel = fuelData.value.data;
}

const { data: prospectusResponse } = await useAsyncData((app) =>
  app.$apiDrive.use(merchApiDrive).prospectus(id)
);
if (prospectusResponse.value && prospectusResponse.value.data) {
  prospectus = prospectusResponse.value.data;
}

const { data: shopPushZonesResponse } = await useAsyncData((app) =>
  app.$apiDrive.use(merchApiDrive).shop(id)
);
if (
  shopPushZonesResponse.value &&
  shopPushZonesResponse.value.attributes &&
  shopPushZonesResponse.value.attributes.push_zones
) {
  shopPushZones = shopPushZonesResponse.value.attributes.push_zones;
}

const { data: articlesBlogresponse } = await useAsyncData((app) =>
  app.$apiDrive.use(blogMagasinApiDrive).home(id)
);
if (articlesBlogresponse.value && articlesBlogresponse.value.data) {
  articlesBlog = articlesBlogresponse.value.data;
}

useHead(() => {
  return {
    title:
      "Cora devient Carrefour " +
      magasin.attributes.magasin +
      " | Magasin, Drive et Livraison",
    meta: [
      {
        hid: "description",
        name: "description",
        content:
          "Horaires, prospectus et actualité de votre supermarché " +
          magasin.attributes.magasin +
          ". Découvrez le catalogue des produits disponibles en magasin, drive ou livraison à domicile."
      }
    ],
    link: [
      {
        rel: "canonical",
        href: `/magasins/${magasin.attributes.aliasWeb}-m-${magasin.id}`
      }
    ]
  };
});

const { setStoreLocatorContext } = useDatalayerStore();

const changeShop = async (storeId) => {
  await switchMagasin(storeId);
  if (nbProduits.value > 0) {
    displayChangeShopPopin("fiche-magasin");
  }
};

const openOrcloseStoreLocator = () => {
  setStoreLocatorContext("Fiche magasin");
  displayOrCloseStoreLocatorPopin();
};

watch(
  () => authStatus.value,
  (value) => {
    if (
      (value === StatusTypes.LOGIN_SUCCESS ||
        value === StatusTypes.LOGOUT_SUCCESS) &&
      !isBascule.value
    ) {
      refreshMagasin.value = true;
    }
  }
);
</script>

<template>
  <component
    :is="component"
    :prospectus="prospectus"
    :fuel="fuel"
    :magasin="magasin"
    :push-zones="shopPushZones"
    :articles="articlesBlog"
    :attached-shop-id="currentMagasin"
    :is-pro="client ? client.attributes.pro : false"
    :is-flockler-loaded="isFlocklerLoaded"
    @chooseShopClicked="changeShop"
    @openOrcloseStoreLocator="openOrcloseStoreLocator"
  />
</template>
